import {
  type CreatePlacesValidateAddressResponse,
  useCreateJoinWaitlist,
  useCreatePlacesValidateAddress,
  useUpdateCheckoutPurchaseIntentsByPurchaseIntentId,
} from "@birdwatch/client";

import { useAddPlace, usePlaceCheck } from "@/features/my/places/shell";
import {
  isPresaleZipcode,
  PRESALE_CAMPAIGN,
  PRESALE_PURCHASE_INTENT_FLOW,
} from "@/features/shared/presale";
import { useRefresh } from "@/features/shared/realtime-refresh";

/** The address a funnel step collects, in the field names every place endpoint uses. */
export type PresaleAddress = {
  streetAddress: string;
  streetAddressLine2: string;
  city: string;
  state: string;
  zip: string;
  latitude: string;
  longitude: string;
};

/**
 * What checking an address concluded.
 *
 * A discriminated union rather than a bag of optional flags: every caller must handle
 * each outcome, and no combination can exist that the UI has no screen for.
 */
export type PresaleAddressOutcome =
  | { kind: "eligible"; address: PresaleAddress }
  | { kind: "outside-campaign"; address: PresaleAddress; zip: string }
  /** The address resolves to a place that already has an active membership — a dead end. */
  | { kind: "already-active"; address: PresaleAddress; placeId: string }
  | { kind: "unrecognized"; message: string };

/** Google's standardized address, when it gave us one — otherwise what was typed. */
function reconcile(
  address: PresaleAddress,
  validation: CreatePlacesValidateAddressResponse,
): PresaleAddress {
  return {
    ...address,
    streetAddress: validation.streetAddress || address.streetAddress,
    streetAddressLine2:
      validation.streetAddressLine2 || address.streetAddressLine2,
    city: validation.city || address.city,
    state: validation.state || address.state,
    zip: validation.zip || address.zip,
    latitude:
      validation.latitude === null
        ? address.latitude
        : String(validation.latitude),
    longitude:
      validation.longitude === null
        ? address.longitude
        : String(validation.longitude),
  };
}

/**
 * Step 2's three backend calls, behind one interface.
 *
 * Eligibility is decided against the CAMPAIGN's zipcodes, not the platform's
 * `isInServiceArea`: the presale sells one market's founding offer, and the service
 * area flag is a global kill-switch that is off unless Statsig says otherwise
 * (`address-validation.service.ts`) — gating on it would let any US address buy.
 */
export function usePresalePlace() {
  const refresh = useRefresh();
  // `publish: false` route: a geocoding lookup that writes nothing.
  const validateAddress = useCreatePlacesValidateAddress();
  const checkPlaceIsActive = usePlaceCheck();
  const addPlace = useAddPlace();
  // `publish: false` route: a waitlist lead no screen lists.
  const joinWaitlist = useCreateJoinWaitlist();
  const updateIntent = useUpdateCheckoutPurchaseIntentsByPurchaseIntentId({
    // The route announces the intent, and — for a presale flow, which is the only
    // one this funnel stamps — the account whose acquisition trackings it records.
    onSuccess: (intent) => {
      void refresh("purchaseIntent.changed", { purchaseIntentId: intent.id });
      if (intent.accountId) {
        void refresh("account.changed", { accountId: intent.accountId });
      }
    },
  });

  return {
    isChecking: validateAddress.isPending || checkPlaceIsActive.isPending,
    isAdding: addPlace.isPending || updateIntent.isPending,
    isJoiningWaitlist: joinWaitlist.isPending,

    /** Validates the address and decides whether it can buy the founding offer. */
    check: async (address: PresaleAddress): Promise<PresaleAddressOutcome> => {
      const validation = await validateAddress.mutateAsync({
        streetAddress: address.streetAddress,
        streetAddressLine2: address.streetAddressLine2 || "",
        city: address.city,
        state: address.state,
        zip: address.zip,
      });
      // Safe on the invalid path too — every field falls back to what was typed.
      const resolved = reconcile(address, validation);

      // Before the validity branch AND before the campaign gate. `placeId` is OUR
      // `Place.id` (the platform looks it up from the typed address first, then from
      // Google's standardization), so it is present even when Google rejected the address.
      // A home we already service must be told that rather than offered the waitlist —
      // that screen promises to notify them when we reach their area, which is the wrong
      // thing to say to an active member and hides the real problem.
      if (validation.placeId) {
        const { isActive } = await checkPlaceIsActive.mutateAsync(
          validation.placeId,
        );
        if (isActive) {
          return {
            kind: "already-active",
            address: resolved,
            placeId: validation.placeId,
          };
        }
      }

      if (!validation.isValid) {
        return {
          kind: "unrecognized",
          message:
            validation.validationError ||
            "We don't recognize that address. Please check it and try again.",
        };
      }

      if (!isPresaleZipcode(resolved.zip, PRESALE_CAMPAIGN)) {
        return {
          kind: "outside-campaign",
          address: resolved,
          zip: resolved.zip,
        };
      }
      return { kind: "eligible", address: resolved };
    },

    /**
     * The buyer insisted an unrecognized address is correct.
     *
     * The gate runs against the TYPED zip — an unrecognized address has no
     * USPS-standardized one, so there is nothing else to gate on. That asymmetry with
     * `check` is deliberate: unify the two and an override gets gated on an empty string.
     * No network call either — the duplicate check already ran on these exact values (the
     * page drops the outcome the moment any of them changes), and re-validating an address
     * the buyer has overruled would spend another billed lookup to get the same answer.
     */
    confirmUnrecognized: (address: PresaleAddress): PresaleAddressOutcome =>
      isPresaleZipcode(address.zip, PRESALE_CAMPAIGN)
        ? { kind: "eligible", address }
        : { kind: "outside-campaign", address, zip: address.zip },

    /**
     * Creates the place, the purchase intent for the offer, and stamps the funnel on it.
     *
     * `add-my-place` writes the place and the intent in one transaction and returns both
     * ids — passing `checkoutLinkId` is what binds the intent to the campaign's cart, so
     * the checkout step has a real intent to read and update. It takes no `flow`, so the
     * follow-up `PUT` is the only way to record which funnel created the intent —
     * `findPresaleResumes` reads exactly that to recognize its own reservations.
     */
    reserve: async (
      address: PresaleAddress,
      campaign: {
        checkoutLinkId?: string;
        discountCode?: string;
        referId?: string;
        referredBy?: string;
      },
    ) => {
      const created = await addPlace.mutateAsync({
        streetAddress: address.streetAddress,
        streetAddressLine2: address.streetAddressLine2 || undefined,
        city: address.city,
        state: address.state,
        zip: address.zip,
        latitude: address.latitude || undefined,
        longitude: address.longitude || undefined,
        checkoutLinkId: campaign.checkoutLinkId || undefined,
        discountCode: campaign.discountCode || undefined,
        referId: campaign.referId || undefined,
        referredBy: campaign.referredBy || undefined,
      });

      // Best-effort by design: the place and the intent are already committed, so throwing
      // here would surface "We couldn't add that home" for a home that WAS added and send
      // the buyer back to create a duplicate. The checkout step re-stamps a miss.
      try {
        await updateIntent.mutateAsync({
          params: { purchaseIntentId: created.purchaseIntentId },
          body: { flow: PRESALE_PURCHASE_INTENT_FLOW },
        });
      } catch {
        // Intentionally swallowed — see above.
      }

      return created;
    },

    /** The consolation path for an address outside the campaign's market. */
    waitlist: (email: string, address: PresaleAddress) =>
      joinWaitlist.mutateAsync({
        email,
        address: [
          address.streetAddress,
          address.streetAddressLine2,
          address.city,
          address.state,
          address.zip,
        ]
          .filter(Boolean)
          .join(", "),
      }),
  };
}
